import {
  getProductsQueryOptions,
  GetProductsResponse,
} from '@features/accounts/api';
import { Account, Product } from '@features/accounts/types';
import {
  getBanksQueryOptions,
  GetBanksResponse,
} from '@features/performance/api/get-banks';
import { api } from '@src/lib/api-client';
import { queryClient, QueryConfig } from '@src/lib/react-query';
import { queryOptions, useQuery } from '@tanstack/react-query';

export type GetAccountResponse = Account;

export async function getAccount(uuid: string): Promise<GetAccountResponse> {
  const account = await api.get<GetAccountResponse>(
    `/api/v3/user/accounts/${uuid}`,
  );

  const products: GetProductsResponse = await queryClient.ensureQueryData({
    queryKey: getProductsQueryOptions.queryKey,
    queryFn: getProductsQueryOptions.queryFn,
  });

  const banks: GetBanksResponse = await queryClient.ensureQueryData({
    queryKey: getBanksQueryOptions.queryKey,
    queryFn: getBanksQueryOptions.queryFn,
  });

  const foundProduct = [...products.checking, ...products.savings].find(
    (product: Product) =>
      product.uuid.toLowerCase() === account.data.productUuid.toLowerCase(),
  );

  const foundBank = [...banks.savings, ...banks.checking].find(
    (bank) => bank.uuid.toLowerCase() === foundProduct?.bankUuid.toLowerCase(),
  );

  const response: Account = {
    ...account.data,
    name: foundProduct?.name ?? '',
    fdicNumber: foundBank?.fdicNumber ?? '',
    numBasisPoints: foundProduct?.numBasisPoints ?? 0,
    numTiebreakerPoints: foundProduct?.numTiebreakerPoints ?? 0,
    preferredRate: foundProduct?.preferredRate ?? false,
    poweredBy: foundProduct?.poweredBy ?? '',
    bankMaximumBalanceCents: foundProduct?.maximumBalanceCents ?? null,
  };
  // Update accounts list cache
  queryClient.setQueryData(['accounts'], (oldData: Account[]) => {
    if (oldData) {
      return oldData.map((oldAccount) =>
        oldAccount.uuid === uuid ? response : oldAccount,
      );
    }
  });

  return response;
}

export const getAccountQueryOptions = (uuid: string) => {
  return queryOptions({
    queryKey: ['accounts', uuid],
    queryFn: () => getAccount(uuid),
  });
};

type UseGetAccountOptions = {
  uuid: string;
  queryConfig?: QueryConfig<typeof getAccountQueryOptions>;
};

export const useGetAccount = ({ uuid, queryConfig }: UseGetAccountOptions) => {
  return useQuery({ ...getAccountQueryOptions(uuid), ...queryConfig });
};
